"use client";

import { useEffect } from "react";

import { initHoneycomb } from "@medbillai/app/lib/honeycomb";

// Although "useEffect" will only run the hook once per mount, hot reloading can
// cause us to mount multiple times, so track initialization separately to avoid
// error noise.
let initialized = false;

/**
 * Deployed stacks set `NEXT_PUBLIC_DEPLOYMENT_ENVIRONMENT` to the stack name
 * (see `infra/programs/nextjs.ts`); `developer` is the local sentinel this repo
 * uses elsewhere (`sentry.config.ts`), so it does not count as deployed. Laptop
 * traffic is what put the Honeycomb account over its event limit. See
 * docs/TELEMETRY.md.
 *
 * Takes its inputs as arguments so callers read the `NEXT_PUBLIC_*` vars
 * literally and Next can inline them at build time.
 */
export const isBrowserExportEnabled = ({
  deploymentEnvironment,
  localOptIn,
}: {
  deploymentEnvironment: string | undefined;
  localOptIn: string | undefined;
}): boolean =>
  localOptIn === "1" ||
  (Boolean(deploymentEnvironment) && deploymentEnvironment !== "developer");

/**
 * A hook that initializes the Honeycomb observability library.
 * Only intended for the nextjs app, not the react native app.
 */
export function useObservability() {
  useEffect(() => {
    if (initialized) {
      return;
    }
    initialized = true;

    const exportEnabled = isBrowserExportEnabled({
      deploymentEnvironment: process.env.NEXT_PUBLIC_DEPLOYMENT_ENVIRONMENT,
      localOptIn: process.env.NEXT_PUBLIC_OTEL_LOCAL_EXPORT,
    });
    // Returning here skips browser instrumentation entirely, so no spans or
    // trace context are created either. `HoneycombWebSDK` owns its exporter
    // with no seam to swap in a no-op, and the API and Expo gates keep their
    // spans. See docs/TELEMETRY.md for why web differs.
    if (!exportEnabled) {
      console.info(
        "[Honeycomb] browser telemetry disabled locally. Set NEXT_PUBLIC_OTEL_LOCAL_EXPORT=1 to enable.",
      );
      return;
    }

    const apiKey = process.env.NEXT_PUBLIC_HONEYCOMB_API_KEY;
    if (!apiKey) {
      console.error(
        "[Honeycomb] NEXT_PUBLIC_HONEYCOMB_API_KEY missing — telemetry disabled",
      );
      return;
    }
    initHoneycomb({
      apiKey,
      serviceName:
        process.env.NEXT_PUBLIC_HONEYCOMB_SERVICE_NAME || "granted-next",
      // Opt-in: debug + localVisualizations flood DevTools with per-span
      // Honeycomb URLs. Set NEXT_PUBLIC_HONEYCOMB_DEBUG=1 when investigating.
      debug: process.env.NEXT_PUBLIC_HONEYCOMB_DEBUG === "1",
    });
  }, []);
}
